import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { MetricValueRecords } from '../../../types/metric-value-records.interface';
import { MetricMilestones } from '../../../types/metric-milestone.interface';
import { MetricsUtilsService } from '../../../services/metrics-utils.service';
import { AmchartsService } from 'app/shared/services/amcharts.service';
import { DecimalPipe } from '@angular/common';
import { parseDateString } from '../../containers/campaign-details/date-operations';

@Component({
  selector: 'metric-cpo-chart',
  templateUrl: './metric-cpo-chart.component.html',
  styleUrls: ['./metric-cpo-chart.component.scss']
})
export class MetricCpoChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() data: MetricValueRecords<number> = [];
  @Input() milestones: MetricMilestones;
  @Input() startDate: string;
  @Input() currency: string;
  @Input() targetCPO: number;
  @Input() hideIcon: boolean = false;
  @Input() showTooltip: boolean = false;
  @Input() tooltipValue: string = '';
  @Input() type: string = '';
  @Input() public lastUpdatedInfo: string;
  @ViewChild('chartRef', { static: true }) chartRef: ElementRef;

  private chart: any;
  private trendSeries: any;
  private am4core;
  private am4charts;

  constructor(
    private readonly zone: NgZone,
    private readonly amChartsService: AmchartsService,
    private readonly metricsUtilsService: MetricsUtilsService,
    private readonly decimalPipe: DecimalPipe
  ) { }

  ngAfterViewInit() {
    this.zone.runOutsideAngular(() => {
      this.amChartsService.importModules()
        .then(modules => {
          if (!modules) {
            return;
          }
          this.am4core = modules.am4core;
          this.am4charts = modules.am4charts;
          this.disposeChart();
          this.initChart();
        });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const dataAvailable = this.data && this.milestones && this.startDate;
    const amChartsAvailable = this.am4core && this.am4charts;

    if (!(dataAvailable && amChartsAvailable)) {
      return;
    }

    if (changes.data || changes.milestones || changes.startDate || changes.targetCPO) {
      this.zone.runOutsideAngular(() => {
        setTimeout(() => {
          this.disposeChart();
          this.initChart();
        }, 0)
      });
    }
  }

  ngOnDestroy() {
    this.zone.runOutsideAngular(() => {
      this.disposeChart();
    });
  }

  private getStartDate() {
    return this.metricsUtilsService.getChartStartDate(this.data, parseDateString(this.startDate));
  }

  private getTrendData() {
    return [
      {
        targetValue: this.targetCPO,
        date: this.getStartDate()
      },
      {
        targetValue: this.targetCPO,
        date: this.amChartsService.getChartEndDate(this.milestones)
      }
    ];
  }

  private createDateAxis() {
    const dateAxis = this.chart.xAxes.push(new this.am4charts.DateAxis());
    const startDate = this.getStartDate();
    const endDate = this.amChartsService.getChartEndDate(this.milestones);

    this.amChartsService.configureDateAxis(dateAxis, startDate, endDate);
    this.amChartsService.createDateAxisRanges(dateAxis, startDate, endDate);

    return dateAxis;
  }

  private createValueAxis() {
    const valueAxis = this.chart.yAxes.push(new this.am4charts.ValueAxis());

    this.amChartsService.configureValueAxis(valueAxis);
    this.amChartsService.configureTicks(valueAxis.renderer.ticks.template);
    this.amChartsService.addCurrencyToAxisLabels(this.currency, valueAxis);

    return valueAxis;
  }

  private createSeries() {
    const targetCPO = this.decimalPipe.transform(this.targetCPO, '1.2-2')
    const series = new this.am4charts.LineSeries();
    this.amChartsService.configureMainSeries(series);
    this.amChartsService.configureTooltip(series);
    series.dx = -1;
    series.tooltipText = `[bold]${this.currency}{value.formatNumber('#,###.00')} (vs. ${this.currency}${targetCPO} target)
      on {timestamp}`;

    const bullet = this.amChartsService.createBullet(series, 4);
    series.bullets.push(bullet);
    series.bulletsContainer.parent = this.chart.seriesContainer;

    this.chart.series.push(series);

    return series;
  }

  private createTrendLine() {
    const trend = this.chart.series.push(new this.am4charts.LineSeries());

    this.amChartsService.configureTrendLine(trend);
    trend.dataFields.valueY = 'targetValue';
    trend.dataFields.dateX = 'date';
    trend.data = this.getTrendData();
    trend.zIndex = 1;
    this.trendSeries = trend;

    return trend;
  };

  disposeChart() {
    this.chart = null;
  }

  initChart() {
    if (!this.chart) {
      this.chart = this.am4core.create(this.chartRef.nativeElement, this.am4charts.XYChart);
    }
    this.chart.data = this.data;
    this.amChartsService.configureChart(this.chart);
    this.createDateAxis();
    this.createValueAxis();
    this.createSeries();
    this.createTrendLine();
    this.chart.cursor = this.amChartsService.createCursor();
  }

  public refreshChart() {
    this.zone.runOutsideAngular(() => {
      this.disposeChart();
      this.initChart();
    })
  }
}
